/**
 * FamilyUpcoming — Upcoming Anniversaries widget for the Family
 * Timeline tab. Computes next-90-days of birthdays, wedding
 * anniversaries and death anniversaries from the tree's members and
 * relationships. Each item can be one-tap shared to WhatsApp so the
 * family group gets a friendly reminder.
 *
 * Also renders a small "On This Day in India" strip showing curated
 * cultural / historical events that share today's month+day.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellRing, Cake, Flower2, Heart, MessageCircle, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ── On-this-day: curated Indian cultural / historical events. Keyed
// by "MM-DD". Kept small and additive; future work can enrich from
// LLM. Every entry is a plain-English sentence the family can share
// as a "did you know?" nugget alongside their own anniversaries.
const ON_THIS_DAY = {
  "01-01": [{ label: "New Year's Day", note: "Start of the Gregorian calendar year.", emoji: "🎉" }],
  "01-14": [{ label: "Makar Sankranti / Pongal", note: "Harvest festival across India — Pongal in Tamil Nadu, Uttarayan in Gujarat.", emoji: "🌾" }],
  "01-26": [{ label: "Republic Day", note: "India adopted its constitution in 1950.", emoji: "🇮🇳" }],
  "02-14": [{ label: "Valentine's Day", note: "A day for love and companionship.", emoji: "❤️" }],
  "03-08": [{ label: "International Women's Day", note: "Celebrate the women in your family — mothers, sisters, daughters.", emoji: "💐" }],
  "04-13": [{ label: "Baisakhi / Vaisakhi", note: "Sikh New Year and harvest festival.", emoji: "🌾" }],
  "04-14": [{ label: "Dr B.R. Ambedkar Jayanti", note: "Architect of the Indian Constitution (1891).", emoji: "🇮🇳" }],
  "04-14": [{ label: "Tamil New Year / Vishu / Puthandu", note: "New Year in Tamil Nadu, Kerala and much of South India.", emoji: "🌸" }],
  "05-01": [{ label: "May Day", note: "International Workers' Day.", emoji: "🛠️" }],
  "05-06": [{ label: "Rabindranath Tagore Jayanti", note: "Nobel-laureate poet (1861).", emoji: "🪶" }],
  "06-21": [{ label: "International Yoga Day", note: "Proclaimed by the UN in 2014 on India's initiative.", emoji: "🧘" }],
  "07-01": [{ label: "Doctors' Day (India)", note: "Honours Dr Bidhan Chandra Roy.", emoji: "🩺" }],
  "08-15": [{ label: "Independence Day", note: "India gained independence in 1947.", emoji: "🇮🇳" }],
  "08-29": [{ label: "National Sports Day", note: "Birthday of Major Dhyan Chand (1905).", emoji: "🏑" }],
  "09-05": [{ label: "Teachers' Day (India)", note: "Dr S. Radhakrishnan's birthday (1888).", emoji: "📚" }],
  "09-14": [{ label: "Hindi Diwas", note: "Adoption of Hindi as an official language of India (1949).", emoji: "🗣️" }],
  "10-02": [{ label: "Gandhi Jayanti", note: "Mahatma Gandhi's birthday (1869).", emoji: "🕊️" }],
  "10-31": [{ label: "Sardar Patel Jayanti", note: "Iron Man of India (1875) — National Unity Day.", emoji: "🇮🇳" }],
  "11-14": [{ label: "Children's Day (India)", note: "Jawaharlal Nehru's birthday (1889).", emoji: "🎈" }],
  "11-19": [{ label: "Indira Gandhi Jayanti", note: "India's first woman Prime Minister (1917).", emoji: "🇮🇳" }],
  "12-06": [{ label: "Dr B.R. Ambedkar Mahaparinirvan", note: "Passing anniversary of Dr Ambedkar (1956).", emoji: "🕯️" }],
  "12-23": [{ label: "Kisan Diwas (Farmers' Day)", note: "Chaudhary Charan Singh's birthday (1902).", emoji: "🌾" }],
  "12-25": [{ label: "Christmas", note: "Celebrated across India — especially Kerala & Goa.", emoji: "🎄" }],
  "12-31": [{ label: "New Year's Eve", note: "Last day of the Gregorian year.", emoji: "🥂" }],
};

// Format a "MM-DD" string from a Date.
function mmdd(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}-${day}`;
}

// Parse an ISO date-only string into {month, day, year} — returns
// null if the string doesn't look like a date at all.
function parseDMY(s) {
  if (!s) return null;
  const t = String(s);
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10), day: parseInt(m[3], 10) };
  // Accept "YYYY" too — year-only, no MMDD available
  const y = t.match(/^(\d{4})$/);
  if (y) return { year: parseInt(y[1], 10), month: null, day: null };
  return null;
}

// Compute the next occurrence date of a MM-DD anniversary starting
// today. Returns a Date object.
function nextAnniversary(month, day, today) {
  if (!month || !day) return null;
  const y = today.getFullYear();
  let candidate = new Date(y, month - 1, day);
  // Handle Feb-29 edge — fall back to Feb 28 in non-leap years.
  if (candidate.getMonth() !== month - 1) {
    candidate = new Date(y, month - 1, day - 1);
  }
  if (candidate < today) {
    candidate = new Date(y + 1, month - 1, day);
    if (candidate.getMonth() !== month - 1) {
      candidate = new Date(y + 1, month - 1, day - 1);
    }
  }
  return candidate;
}

function daysUntil(target, today) {
  const ms = target - today;
  return Math.max(0, Math.round(ms / 86400000));
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Bright accent per event type — keeps the widget scannable at a glance.
const TYPE_STYLE = {
  birthday:  { color: "#2C7A4B", icon: Cake,     label: "Birthday" },
  wedding:   { color: "#B25D38", icon: Heart,    label: "Wedding anniversary" },
  death:     { color: "#5B4A73", icon: Flower2,  label: "Remembrance anniversary" },
  milestone: { color: "#3B1F0A", icon: Sparkles, label: "Milestone" },
};

// ── Memory Prompts opt-in — gentle email reminders before family dates,
// nudging relatives to add a memory. Backed by /family-anniversaries/prefs
// + the existing weekly digest scheduler.
function MemoryPromptsOptIn() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    http.get("/family-anniversaries/prefs").then((r) => setPrefs(r.data)).catch(() => {});
  }, [user]);
  if (!user || !prefs) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await http.patch("/family-anniversaries/prefs", {
        enabled: !prefs.enabled,
        window_days: prefs.window_days || 30,
      });
      setPrefs((p) => ({ ...p, enabled: data.enabled }));
      toast.success(data.enabled
        ? "Memory prompts on — we'll email gentle reminders before family dates."
        : "Memory prompts turned off.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update — try again.");
    } finally { setBusy(false); }
  };

  const changeFreq = async (e) => {
    const frequency = e.target.value;
    try {
      const { data } = await http.patch("/family-anniversaries/prefs", {
        enabled: true, window_days: prefs.window_days || 30, frequency,
      });
      setPrefs((p) => ({ ...p, frequency: data.frequency || frequency }));
      toast.success(frequency === "weekly"
        ? "You'll get one gentle weekly digest."
        : "You'll get a reminder the day before each family date.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update — try again.");
    }
  };

  const sendTest = async () => {
    try {
      await http.post("/family-anniversaries/send-test");
      toast.success("Test email sent — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the test email.");
    }
  };

  return (
    <div
      className="mb-4 rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap"
      style={{ borderColor: "#E8DFCF", background: "#FFF7ED" }}
      data-testid="memory-prompts-optin"
    >
      <BellRing className="w-4 h-4 flex-shrink-0" style={{ color: "#B25D38" }} />
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-semibold">Memory prompts</div>
        <div className="text-xs opacity-70">Gentle email reminders before birthdays, anniversaries and remembrance days — so no one forgets to add a memory.</div>
      </div>
      <div className="flex items-center gap-2">
        {prefs.enabled && (
          <select
            value={prefs.frequency || "weekly"} onChange={changeFreq}
            className="text-xs rounded-lg border px-2 py-1.5 bg-white"
            style={{ borderColor: "#E8DFCF" }}
            data-testid="memory-prompts-frequency"
          >
            <option value="weekly">Weekly digest</option>
            <option value="day_before">Day before each date</option>
          </select>
        )}
        {prefs.enabled && (
          <button type="button" onClick={sendTest} className="text-xs underline opacity-70 hover:opacity-100" data-testid="memory-prompts-test">
            Send me a test
          </button>
        )}
        <button
          type="button" onClick={toggle} disabled={busy}
          className="rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-sm disabled:opacity-60 transition"
          style={{ background: prefs.enabled ? "#16A34A" : "#9A8B7A" }}
          data-testid="memory-prompts-toggle"
        >
          {prefs.enabled ? "On ✓" : "Turn on"}
        </button>
      </div>
    </div>
  );
}

export default function FamilyUpcoming({ members = [], rels = [], events = [], treeName = "our family", treeSlug = "", windowDays = 90 }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const items = useMemo(() => {
    const out = [];
    const nameOf = (id) => {
      const mem = members.find((x) => x.id === id);
      return mem ? [mem.first_name, mem.last_name].filter(Boolean).join(" ") : "Family member";
    };

    for (const m of members) {
      const b = parseDMY(m.birth_date);
      const d = parseDMY(m.death_date);
      const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
      const isDeceased = !!m.death_date;
      // Living members → birthday reminders
      if (!isDeceased && b?.month && b?.day) {
        const next = nextAnniversary(b.month, b.day, today);
        if (next) {
          const du = daysUntil(next, today);
          if (du <= windowDays) {
            const age = b.year ? (next.getFullYear() - b.year) : null;
            out.push({
              date: next,
              daysUntil: du,
              type: "birthday",
              title: `${nm}'s birthday`,
              subtitle: age ? `Turns ${age}` : "",
              memberId: m.id,
              memorialSlug: m.memorial_slug,
              shareText: age
                ? `🎂 ${nm} turns ${age} on ${next.toDateString().slice(4)} — send them love from ${treeName}! ${window?.location?.origin || ""}${treeSlug && m.id ? `/family/${treeSlug}/member/${m.id}` : ""}`
                : `🎂 ${nm}'s birthday is on ${next.toDateString().slice(4)} — send them love from ${treeName}!`,
            });
          }
        }
      }
      // Deceased members → death anniversary
      if (isDeceased && d?.month && d?.day) {
        const next = nextAnniversary(d.month, d.day, today);
        if (next) {
          const du = daysUntil(next, today);
          if (du <= windowDays) {
            const yrs = d.year ? (next.getFullYear() - d.year) : null;
            out.push({
              date: next,
              daysUntil: du,
              type: "death",
              title: `${nm} — remembrance anniversary`,
              subtitle: yrs ? `${ordinal(yrs)} year in loving memory` : "In loving memory",
              memberId: m.id,
              memorialSlug: m.memorial_slug,
              shareText: yrs
                ? `🕯️ Today marks ${yrs} ${yrs === 1 ? "year" : "years"} since ${nm} left us. Remembering their life & legacy. ${m.memorial_slug ? `${window?.location?.origin || ""}/celebrate/${m.memorial_slug}` : ""}`
                : `🕯️ Remembering ${nm} today. ${m.memorial_slug ? `${window?.location?.origin || ""}/celebrate/${m.memorial_slug}` : ""}`,
            });
          }
        }
      }
    }

    // Wedding anniversaries — from spouse relationships with wedding_date
    for (const r of rels) {
      if (r.type !== "spouse") continue;
      const w = parseDMY(r.wedding_date);
      if (!w?.month || !w?.day) continue;
      const next = nextAnniversary(w.month, w.day, today);
      if (!next) continue;
      const du = daysUntil(next, today);
      if (du > windowDays) continue;
      const yrs = w.year ? (next.getFullYear() - w.year) : null;
      const p1 = nameOf(r.person1_id);
      const p2 = nameOf(r.person2_id);
      out.push({
        date: next,
        daysUntil: du,
        type: "wedding",
        title: `${p1} & ${p2} — wedding anniversary`,
        subtitle: yrs ? `${ordinal(yrs)} anniversary` : "Anniversary",
        shareText: yrs
          ? `💐 ${p1} & ${p2} celebrate ${yrs} ${yrs === 1 ? "year" : "years"} of marriage on ${next.toDateString().slice(4)}. Congratulations from ${treeName}!`
          : `💐 ${p1} & ${p2}'s anniversary is on ${next.toDateString().slice(4)}. Congratulations from ${treeName}!`,
      });
    }

    // Custom milestones — events with event_type = "milestone" that have
    // a recurring MM-DD (annual). Non-milestone events already show in
    // the chronological timeline below.
    for (const e of events || []) {
      if (String(e.event_type || "").toLowerCase() !== "milestone") continue;
      const p = parseDMY(e.event_date);
      if (!p?.month || !p?.day) continue;
      const next = nextAnniversary(p.month, p.day, today);
      if (!next) continue;
      const du = daysUntil(next, today);
      if (du > windowDays) continue;
      out.push({
        date: next,
        daysUntil: du,
        type: "milestone",
        title: e.title || "Milestone",
        subtitle: e.location || (p.year ? `Since ${p.year}` : ""),
        shareText: `✨ ${e.title || "Family milestone"} — remembering on ${next.toDateString().slice(4)}. From ${treeName}.`,
      });
    }

    return out.sort((a, b) => a.date - b.date);
  }, [members, rels, events, today, windowDays, treeName]);

  // On-this-day events for today through the next ~14 days — a
  // rotating culture strip so it's visible year-round rather than
  // just on the 24 seed dates.
  const onThisDay = useMemo(() => {
    const out = [];
    for (let i = 0; i < 14; i += 1) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const key = mmdd(d);
      const bag = ON_THIS_DAY[key];
      if (bag) {
        for (const ev of bag) out.push({
          ...ev,
          when: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toDateString().slice(0, 10),
        });
      }
    }
    return out.slice(0, 6);
  }, [today]);

  const share = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (items.length === 0 && onThisDay.length === 0) return null;

  return (
    <div className="mb-8" data-testid="family-upcoming">
      <MemoryPromptsOptIn />
      {items.length > 0 && (
        <div className="rounded-2xl border p-5" style={{ borderColor: "#E8DFCF", background: "linear-gradient(135deg, #FFFDF8 0%, #FFF3D6 100%)" }}>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#B25D38", color: "#FFF3D6" }}>
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#B25D38" }}>Upcoming · next {windowDays} days</div>
              <div className="font-display text-2xl mt-0.5" style={{ color: "#3D2A17" }}>Family anniversaries</div>
              <div className="text-xs mt-1" style={{ color: "#7A4A1F" }}>
                Birthdays, weddings and remembrance days for {treeName}. Tap <MessageCircle className="w-3 h-3 inline" /> to share a reminder on WhatsApp.
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it, idx) => {
              const style = TYPE_STYLE[it.type] || TYPE_STYLE.milestone;
              const Ic = style.icon;
              return (
                <div
                  key={`${it.type}-${idx}`}
                  data-testid={`family-upcoming-item-${it.type}-${idx}`}
                  className="rounded-xl border p-3 flex flex-col gap-2"
                  style={{ borderColor: "#E8DFCF", background: "#FFFDF8", borderLeftWidth: 3, borderLeftColor: style.color }}
                >
                  <div className="flex items-start gap-2">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${style.color}22`, color: style.color }}>
                      <Ic className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: style.color }}>
                        {it.daysUntil === 0 ? "Today" : it.daysUntil === 1 ? "Tomorrow" : `in ${it.daysUntil} days`} · {style.label}
                      </div>
                      <div className="text-sm font-semibold mt-0.5 leading-tight truncate" title={it.title}>{it.title}</div>
                      {it.subtitle && <div className="text-xs opacity-70">{it.subtitle}</div>}
                      <div className="text-[10px] font-mono opacity-60 mt-0.5">{it.date.toDateString().slice(0, 10)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {it.memorialSlug ? (
                      <Link to={`/celebrate/${it.memorialSlug}`} data-testid={`family-upcoming-open-memorial-${idx}`} className="text-[11px] font-medium inline-flex items-center gap-1 hover:underline" style={{ color: style.color }}>
                        <Flower2 className="w-3 h-3" /> Memorial
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => share(it.shareText)}
                      data-testid={`family-upcoming-share-${idx}`}
                      className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full transition"
                      style={{ background: "#25D366", color: "#FFFFFF" }}
                      title="Share this reminder on WhatsApp"
                    >
                      <MessageCircle className="w-3 h-3" /> Share
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {onThisDay.length > 0 && (
        <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: "#E8DFCF", background: "#FFF8EC" }} data-testid="family-onthisday">
          <div className="text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>On this day in India</div>
          <div className="flex flex-wrap gap-2">
            {onThisDay.map((ev, i) => (
              <div
                key={i}
                className="rounded-xl border px-3 py-2 text-xs flex items-start gap-2 max-w-md"
                style={{ borderColor: "#E8DFCF", background: "#FFFDF8" }}
                data-testid={`family-onthisday-item-${i}`}
              >
                <span className="text-lg leading-none">{ev.emoji}</span>
                <div>
                  <div className="font-semibold">{ev.when} · {ev.label}</div>
                  <div className="opacity-80">{ev.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
